import {
  DEFAULT_CITY_OPTION,
  formatCompactMileage,
  formatTemplatePrice,
  formatTransmissionShort,
  getTemplateBadges,
} from "./autoclaroHomeUtils";
import { isVerifiedDealer as resolveDealerVerified } from "../../utils/dealerVerificationDisplay";
import {
  getFairPriceLabelText,
  resolveFairPriceLabel,
} from "../../utils/fairPriceDisplay";
import { FAIR_PRICE_CLASS } from "../../constants/automapFairPriceConstants";
import { LISTING_FALLBACK_COPY } from "../../constants/automapListingConstants";
import { formatLastConfirmedAt, getDealerInitials as listingDealerInitials } from "../../utils/automapListing/listingFormatters";
import {
  budgetValueFromPrices,
  pricesFromBudgetValue,
  SEARCH_BUDGET_OPTION_DEFS,
  SEARCH_MAX_PRICE_NO_LIMIT,
  SEARCH_MAX_PRICE_THRESHOLDS_COP,
  SEARCH_MIN_PRICE_THRESHOLDS_COP,
} from "../../utils/vehicleSearch/vehicleSearchPriceOptions";
import {
  parseVehicleSearchParams,
  serializeVehicleSearchParams,
} from "../../utils/vehicleSearch";

export const AUTOCLARO_SLEEK_SEARCH_LAYOUT = "autoclaro-sleek";

export const SEARCH_BUDGET_OPTIONS = SEARCH_BUDGET_OPTION_DEFS.map((entry) => ({
  ...entry,
  label: entry.value,
}));

export const SEARCH_BODY_OPTIONS = ["Todos", "SUV", "Sedán", "Hatchback", "Pickup"];
export const SEARCH_TRANSMISSION_OPTIONS = ["Todas", "Automática", "Mecánica"];

export const SEARCH_SORT_OPTIONS = [
  { value: "match", label: "Mejor coincidencia", apiSort: "newest" },
  { value: "priceAsc", label: "Precio menor", apiSort: "price_low_to_high" },
  { value: "priceDesc", label: "Precio mayor", apiSort: "price_high_to_low" },
  { value: "yearDesc", label: "Año más reciente", apiSort: "year_new_to_old" },
  { value: "kmAsc", label: "Menor kilometraje", apiSort: "mileage_low_to_high" },
];

export const MIN_PRICE_SIDEBAR_OPTIONS = [
  { label: "Sin mínimo", value: 0 },
  ...SEARCH_MIN_PRICE_THRESHOLDS_COP.map((cop) => ({
    label: `${cop / 1_000_000}M`,
    value: cop,
  })),
];

export const MAX_PRICE_SIDEBAR_OPTIONS = [
  { label: "Sin máximo", value: SEARCH_MAX_PRICE_NO_LIMIT },
  ...SEARCH_MAX_PRICE_THRESHOLDS_COP.map((cop) => ({
    label: `${cop / 1_000_000}M`,
    value: cop,
  })),
];

export { budgetValueFromPrices, pricesFromBudgetValue };

export const YEAR_MIN_OPTIONS = [
  { label: "Cualquier año", value: 0 },
  { label: "2018", value: 2018 },
  { label: "2020", value: 2020 },
  { label: "2022", value: 2022 },
];

export function mapUiSortToApiSort(uiSort) {
  return SEARCH_SORT_OPTIONS.find((option) => option.value === uiSort)?.apiSort || "newest";
}

export function mapApiSortToUiSort(apiSort) {
  return SEARCH_SORT_OPTIONS.find((option) => option.apiSort === apiSort)?.value || "match";
}

export function mapTransmissionFromApi(value) {
  if (!value) return "Todas";
  if (/auto/i.test(value)) return "Automática";
  if (/mec|manual/i.test(value)) return "Mecánica";
  return value;
}

export function parseSearchStateFromParams(searchParams) {
  /** @deprecated Milestone 4 — use parseVehicleSearchParams from utils/vehicleSearch */
  const p = parseVehicleSearchParams(searchParams);
  return {
    search: p.keyword || "",
    city: p.city || DEFAULT_CITY_OPTION,
    budget: budgetValueFromPrices(p.priceMinCop, p.priceMaxCop),
    bodyType: p.bodyType || "Todos",
    transmission: p.transmission ? mapTransmissionFromApi(p.transmission) : "Todas",
    minPrice: p.priceMinCop ?? 0,
    maxPrice: p.priceMaxCop ?? 999_999_999,
    yearMin: p.yearMin ?? 0,
    fairOnly: p.fairPriceLabels?.length > 0,
    verifiedOnly: p.dealerVerified,
    hideRiskAlert: p.hideHighRisk,
    completeInfoOnly: p.completeInfoOnly,
    availabilityConfirmed: p.recentlyConfirmedOnly,
    sort: mapApiSortToUiSort(p.sort),
    page: p.page,
  };
}

export function mapTransmissionFilter(value) {
  if (!value || value === "Todas") return null;
  if (/auto/i.test(value)) return "automatic";
  if (/mec|manual/i.test(value)) return "manual";
  return value;
}

export function buildSearchParamsFromState(state) {
  /** @deprecated Milestone 4 — use serializeVehicleSearchParams from utils/vehicleSearch */
  const p = parseVehicleSearchParams({
    keyword: state.search,
    city: state.city === DEFAULT_CITY_OPTION ? null : state.city,
    priceMinCop: state.minPrice > 0 ? state.minPrice : null,
    priceMaxCop: state.maxPrice < 999_999_999 ? state.maxPrice : null,
    bodyType: state.bodyType === "Todos" ? null : state.bodyType,
    transmission: mapTransmissionFilter(state.transmission),
    yearMin: state.yearMin > 0 ? state.yearMin : null,
    dealerVerified: state.verifiedOnly,
    hideHighRisk: state.hideRiskAlert,
    completeInfoOnly: state.completeInfoOnly,
    recentlyConfirmedOnly: state.availabilityConfirmed,
    fairPriceLabels: state.fairOnly ? ["below_market", "fair_price"] : [],
    sort: mapUiSortToApiSort(state.sort),
    page: state.page,
  });
  return serializeVehicleSearchParams(p);
}

export function buildVehicleApiParamsFromState(state, { profileId, pageSize = 24 } = {}) {
  const params = buildSearchParamsFromState(state);
  const apiParams = Object.fromEntries(params.entries());
  apiParams.pageSize = pageSize;
  if (profileId) apiParams.profile = profileId;
  return apiParams;
}

export function getDealerInitials(name) {
  if (!name) return "AM";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

export function isVerifiedDealer(listing) {
  return resolveDealerVerified(listing);
}

export { resolveDealerVerified as shouldShowListingDealerVerification };

export function formatSearchMoney(cop, currency = "COP", options = {}) {
  return formatTemplatePrice(cop, currency, options);
}

export function buildActiveFilterChips(state) {
  const chips = [];
  if (state.search?.trim()) chips.push({ key: "search", label: `Búsqueda: ${state.search.trim()}` });
  if (state.city && state.city !== DEFAULT_CITY_OPTION) {
    chips.push({ key: "city", label: state.city });
  }
  if (state.bodyType && state.bodyType !== "Todos") {
    chips.push({ key: "bodyType", label: state.bodyType });
  }
  if (state.transmission && state.transmission !== "Todas") {
    chips.push({ key: "transmission", label: state.transmission });
  }
  if (state.minPrice > 0 || state.maxPrice < 999_999_999) {
    const maxLabel =
      state.maxPrice >= 999_999_999 ? "sin máximo" : formatSearchMoney(state.maxPrice);
    chips.push({
      key: "price",
      label: `${formatSearchMoney(state.minPrice || 0)} – ${maxLabel}`,
    });
  }
  if (state.yearMin > 0) chips.push({ key: "yearMin", label: `Desde ${state.yearMin}` });
  if (state.fairOnly) chips.push({ key: "fairOnly", label: "Precio justo" });
  if (state.verifiedOnly) chips.push({ key: "verifiedOnly", label: "Solo verificados" });
  if (state.hideRiskAlert) chips.push({ key: "hideRiskAlert", label: "Sin alertas" });
  if (state.completeInfoOnly) chips.push({ key: "completeInfoOnly", label: "Info completa" });
  if (state.availabilityConfirmed) chips.push({ key: "availabilityConfirmed", label: "Disponibilidad confirmada" });
  return chips;
}

export function listingCardMeta(listing) {
  const badges = getTemplateBadges(listing);
  const fairLabelKey = resolveFairPriceLabel(listing);
  const fairLabel = getFairPriceLabelText(listing);
  const fairClass = FAIR_PRICE_CLASS[fairLabelKey] || "gray";
  const legacyFairBadge = badges.find((badge) =>
    ["Precio justo", "Buen precio"].includes(badge.text)
  );
  const score = listing?.qualityScore ?? listing?.bestValueScore ?? listing?.dataQualityScore ?? listing?.fairPriceScore;
  const freshLabel = formatLastConfirmedAt(listing?.lastConfirmedAt);
  return {
    badges,
    fairLabel,
    fairLabelKey,
    fairClass,
    fairLabelLegacy: legacyFairBadge?.text || fairLabel,
    fairClassLegacy: legacyFairBadge ? legacyFairBadge.className : fairClass,
    score: score != null ? Math.round(Number(score)) : null,
    freshLabel,
    isFresh: /Confirmado hoy|Confirmado ayer/i.test(freshLabel),
    transmission: formatTransmissionShort(listing?.transmission),
    mileage: formatCompactMileage(listing?.mileageKm),
    verified: resolveDealerVerified(listing),
    dealerName: listing?.dealerName || listing?.sellerName || listing?.sourceName || LISTING_FALLBACK_COPY.dealer,
    dealerInitials: listingDealerInitials(listing?.dealerName || listing?.sellerName),
  };
}
